import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';

const useEditorHistory = ({ fabricCanvasRef, setCurrentPage, canvasStates, totalPages }) => {
  const formatCanvasStates = (canvasStates, totalPages) => {
    if (!canvasStates) return {};

    const historyState = {};

    for (let i = 1; i <= totalPages; i++) {
      historyState[i] = {
        history: canvasStates[i]?.objects ? [canvasStates[i]?.objects] : [[]],
        index: 0
      };
    }

    return historyState;
  };

  const [historyState, setHistoryState] = useState({});

  useEffect(() => {
    setHistoryState(formatCanvasStates(canvasStates, totalPages));
  }, [totalPages]);

  const isRestoring = useRef(true);

  const getCurrentPage = () => {
    let currentPage;
    setCurrentPage((page) => {
      currentPage = page;
      return page;
    });
    return currentPage;
  };

  const debouncedPushToHistory = useRef(
    debounce((canvasInstance) => {
      const canvasJson = canvasInstance.toJSON();
      setHistoryState((prev) => {
        const currentPage = getCurrentPage();

        // Get the current page's history or initialize if it doesn't exist
        const pageHistory = prev[currentPage] ? [...prev[currentPage].history] : [];
        const currentIndex = prev[currentPage] ? prev[currentPage].index : -1;

        // if prev is same as current, don't push to history
        if (prev[currentPage]?.history[prev[currentPage]?.index] == canvasJson?.objects)
          return prev;

        const newHistory = pageHistory.slice(0, currentIndex + 1);

        // Add the new state to history
        newHistory.push(canvasJson?.objects);

        return {
          ...prev,
          [currentPage]: {
            history: newHistory,
            index: newHistory.length - 1
          }
        };
      });
    }, 500)
  ).current;

  const loadHistoryState = async (index) => {
    if (!fabricCanvasRef.current) return;

    const currentPage = getCurrentPage();

    const pageState = historyState[currentPage];
    if (!pageState || !pageState.history[index]) return;

    const history = pageState.history[index];

    isRestoring.current = true;

    await fabricCanvasRef.current.renderAll();

    const canvasJson = {
      ...fabricCanvasRef.current.toJSON(),
      objects: history
    };

    await fabricCanvasRef.current.loadFromJSON(canvasJson, () => {
      fabricCanvasRef.current.renderAll();
      isRestoring.current = false;
    });

    setHistoryState((prev) => ({
      ...prev,
      [currentPage]: {
        ...prev[currentPage],
        index
      }
    }));
  };

  // Undo: move one step back in history.
  const undo = useCallback(() => {
    const currentPage = getCurrentPage();

    const pageState = historyState[currentPage];
    if (!pageState || pageState.index <= 0) return; // No more undo steps.

    const newIndex = pageState.index - 1;
    loadHistoryState(newIndex);
  }, [historyState]);

  // Redo: move one step forward in history.
  const redo = useCallback(() => {
    const currentPage = getCurrentPage();

    const pageState = historyState[currentPage];
    if (!pageState || pageState.index >= pageState.history.length - 1) return; // No more redo steps.

    const newIndex = pageState.index + 1;
    loadHistoryState(newIndex);
  }, [historyState]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z') {
        event.preventDefault();
        redo();
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        undo();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [undo, redo]);

  const pushToHistory = (data) => {
    if (!isRestoring.current) {
      debouncedPushToHistory(data);
    }
  };

  return { historyState, undo, redo, pushToHistory, isRestoring };
};

export default useEditorHistory;
